/**
 * Alpine.js Product Component
 * Handles image gallery, quantity controls, option switching
 *
 * Wishlist/Comparison: Uses global stores (no duplicate logic)
 */

export default function productComponent(productData = {}) {
  return {
    // Product data (ensure defaults to prevent undefined errors)
    productId: productData.productId || null,
    productImages: productData.images || [],
    optionImageMap: productData.optionImageMap || {},

    // Gallery state
    currentImageIndex: 0,

    // Video state
    videoPlaying: false,
    videoIframe: '',

    // Quantity controls (always initialized to prevent ReferenceError)
    quantity: 1,
    minQuantity: 1,
    maxQuantity: 999,

    // Loading states (cart only - wishlist/comparison use stores)
    loading: {
      addToCart: false,
    },

    // Computed: Get current item (video or image)
    get currentItem() {
      return this.productImages[this.currentImageIndex] || null;
    },

    // Initialize component
    init() {
      try {

        // Get product data from DOM if not provided
        if (!this.productId) {
          this.loadProductFromDOM();
        }

        // Setup quantity controls (must run early to set reactive data)
        this.setupQuantityControls();

        // Setup gallery
        this.setupGallery();

        // Setup touch swipe for main image gallery
        this.setupGallerySwipe();

        // Setup option image navigation
        this.setupOptionImageNavigation();

        // Setup thumbnail click events
        this.setupThumbnailEvents();

        // Setup tab switching
        this.setupTabs();

        // Analytics: view_item
        const productEl = this.$root.querySelector('.product-detail') || this.$root.closest('.product-detail') || this.$root;
        if (productEl && window.Analytics) {
          window.Analytics.viewItem(productEl);
        }

      } catch (error) {
        // Keep component functional even if init fails
      }
    },

    // Load product data from DOM
    loadProductFromDOM() {
      // Use $root to get the component root element
      const productBox = this.$root.querySelector('#box-product') || this.$root.closest('#box-product');
      if (!productBox) return;

      // Get product ID
      this.productId =
        productBox.dataset.productId || productBox.querySelector('input[name="product_id"]')?.value;

      // Load product images from global variable (if exists)
      if (typeof productImages !== 'undefined') {
        this.productImages = productImages;
      }

      // Load option image map
      if (typeof optionImageMap !== 'undefined') {
        this.optionImageMap = optionImageMap;
      }
    },

    // Setup quantity controls
    setupQuantityControls() {
      try {
        // Use $root to ensure we search from the component root
        const quantityInput = this.$root.querySelector('input[name="quantity"]');

        if (!quantityInput) return;

        // Update reactive properties from DOM
        this.quantity = parseInt(quantityInput.value) || 1;
        this.minQuantity = parseInt(quantityInput.min) || 1;
        this.maxQuantity = parseInt(quantityInput.max) || 999;

        // Watch for changes
        this.$watch('quantity', value => {
          quantityInput.value = Math.max(this.minQuantity, Math.min(this.maxQuantity, value));
        });
      } catch (error) {
        // Quantity controls init failed - defaults still apply
      }
    },

    // Setup gallery functionality
    setupGallery() {
      if (this.productImages.length === 0) return;

      // Auto-switch to first valid image
      this.setActiveImage(0);
    },

    // Setup touch swipe for main product image gallery
    setupGallerySwipe() {
      if (this.productImages.length <= 1) return;

      const container = this.$root.querySelector('#main-image-container');
      if (!container) return;

      let startX = 0;
      let startY = 0;
      let isDragging = false;

      container.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        isDragging = true;
      }, { passive: true });

      container.addEventListener('touchmove', (e) => {
        if (!isDragging) return;

        const diffX = e.touches[0].clientX - startX;
        const diffY = e.touches[0].clientY - startY;

        // Only handle horizontal swipes (prevent hijacking vertical scroll)
        if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
          e.preventDefault();
        }
      }, { passive: false });

      container.addEventListener('touchend', (e) => {
        if (!isDragging) return;
        isDragging = false;

        const endX = e.changedTouches[0].clientX;
        const endY = e.changedTouches[0].clientY;
        const diffX = endX - startX;
        const diffY = endY - startY;

        // Minimum swipe distance: 50px, and must be more horizontal than vertical
        if (Math.abs(diffX) > 50 && Math.abs(diffX) > Math.abs(diffY)) {
          if (diffX < 0) {
            this.nextImage();
          } else {
            this.previousImage();
          }
        }
      }, { passive: true });
    },

    // Navigate to previous image
    previousImage() {
      if (this.productImages.length <= 1) return;

      this.currentImageIndex =
        this.currentImageIndex > 0 ? this.currentImageIndex - 1 : this.productImages.length - 1;

      this.setActiveImage(this.currentImageIndex);
    },

    // Navigate to next image
    nextImage() {
      if (this.productImages.length <= 1) return;

      this.currentImageIndex =
        this.currentImageIndex < this.productImages.length - 1 ? this.currentImageIndex + 1 : 0;

      this.setActiveImage(this.currentImageIndex);
    },

    // Set active image by index
    setActiveImage(index) {
      if (!this.productImages[index]) return;

      this.currentImageIndex = index;
      const itemData = this.productImages[index];

      // Reset video state when switching items
      this.videoPlaying = false;
      this.videoIframe = '';

      // Only update image if current item is an image (not video)
      if (itemData.type === 'image') {
        // Update main image - use $root to ensure we always get the component root element
        const mainImage = this.$root.querySelector('#main-product-image');
        const mainImageLink = this.$root.querySelector('.main-image-link');

        if (mainImage && itemData.main) {
          mainImage.src = itemData.main;

          if (itemData.main_2x) {
            mainImage.srcset = `${itemData.main} 1x, ${itemData.main_2x} 2x`;
          }

          if (mainImageLink && itemData.original) {
            mainImageLink.href = itemData.original;
          }
        }
      }

      // Update thumbnail highlights
      this.updateThumbnailHighlights(index);

      // Dispatch custom event for thumbnail slider to sync
      document.dispatchEvent(new CustomEvent('product-image-changed', {
        detail: { index: index, productId: this.productId }
      }));
    },

    // Update thumbnail highlights (neumorphic design - use shadows not borders)
    updateThumbnailHighlights(activeIndex) {
      // Use $root to ensure we always search from the component root
      const thumbnails = this.$root.querySelectorAll('.gallery-thumbnail, .gallery-thumbnail-mobile');

      thumbnails.forEach((thumb, index) => {
        if (index === activeIndex) {
          thumb.style.borderColor = 'var(--color-accent)';
        } else {
          thumb.style.borderColor = '';
        }
      });
    },

    // Setup option image navigation
    setupOptionImageNavigation() {
      if (!Object.keys(this.optionImageMap).length) return;

      // Listen for option changes on the root element
      this.$root.addEventListener('change', e => {
        if (e.target.matches('input[type="radio"], select')) {
          this.handleOptionChange(e.target);
        }
      });
    },

    // Setup thumbnail click events (for AJAX-loaded content)
    setupThumbnailEvents() {
      const thumbnails = this.$root.querySelectorAll('.gallery-thumbnail, .gallery-thumbnail-mobile');
      thumbnails.forEach((thumb) => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const dataIndex = parseInt(thumb.getAttribute('data-index'));
          if (!isNaN(dataIndex)) {
            this.setActiveImage(dataIndex);
          }
        });
      });
    },

    // Setup tab switching (editorial underline style)
    setupTabs() {
      const tabButtons = this.$root.querySelectorAll('.product-tab');
      const tabPanels = this.$root.querySelectorAll('.tab-panel');

      if (!tabButtons.length || !tabPanels.length) return;

      // Active: accent text + accent border
      const activeClasses = ['text-[var(--color-accent)]', 'border-[var(--color-accent)]'];
      // Inactive: tertiary text + transparent border + hover states
      const inactiveClasses = ['text-[var(--color-text-tertiary)]', 'border-transparent'];
      const hoverClasses = ['hover:text-[var(--color-text-secondary)]', 'hover:border-[var(--color-border)]'];

      tabButtons.forEach(button => {
        button.addEventListener('click', () => {
          const targetTab = button.getAttribute('data-tab');

          // Update button states
          tabButtons.forEach(btn => {
            if (btn.getAttribute('data-tab') === targetTab) {
              btn.classList.remove(...inactiveClasses, ...hoverClasses);
              btn.classList.add(...activeClasses);
              btn.setAttribute('aria-selected', 'true');
            } else {
              btn.classList.remove(...activeClasses);
              btn.classList.add(...inactiveClasses, ...hoverClasses);
              btn.setAttribute('aria-selected', 'false');
            }
          });

          // Update panel visibility
          tabPanels.forEach(panel => {
            if (panel.getAttribute('data-tab') === targetTab) {
              panel.classList.remove('hidden');
            } else {
              panel.classList.add('hidden');
            }
          });
        });
      });
    },

    // Handle option change for image switching
    handleOptionChange(optionElement) {
      let groupId, valueId;

      // For radio buttons, use data attributes (set in PHP template)
      if (optionElement.type === 'radio') {
        groupId = optionElement.getAttribute('data-group-id');
        valueId = optionElement.getAttribute('data-value-id');
      } else if (optionElement.tagName === 'SELECT') {
        groupId = optionElement.name;
        valueId = optionElement.value;
      }

      if (!groupId || !valueId) return;

      // Find image index from map
      const mapKey = `${groupId}-${valueId}`;
      const imageIndex = this.optionImageMap[mapKey];

      if (imageIndex === undefined || !this.productImages[imageIndex]) return;

      this.setActiveImage(imageIndex);
    },

    // Quantity controls
    decreaseQuantity() {
      if (this.quantity > this.minQuantity) {
        this.quantity--;
      }
    },

    increaseQuantity() {
      if (this.quantity < this.maxQuantity) {
        this.quantity++;
      }
    },

    // ========================================
    // WISHLIST & COMPARISON
    // ========================================
    // Note: Wishlist and comparison are handled by global stores.
    // Template uses $store.wishlist and $store.comparison directly.
    // No wrapper methods needed - keeps component simple and consistent.

    // ========================================
    // VIDEO & LIGHTBOX
    // ========================================

    /**
     * Play video (load YouTube iframe)
     */
    playVideo() {
      const currentItem = this.currentItem;
      if (!currentItem || currentItem.type !== 'video' || this.videoPlaying) return;

      const videoId = currentItem.videoId;
      // YouTube embed with minimal UI - no cards, no related videos, no annotations
      this.videoIframe = `
        <iframe
          src="https://www.youtube.com/embed/${videoId}?autoplay=1&mute=0&rel=0&modestbranding=1&iv_load_policy=3&controls=1&fs=1&disablekb=0&playsinline=1"
          class="w-full h-full"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen>
        </iframe>
      `;
      this.videoPlaying = true;
    },

    /**
     * Open lightbox for images (not videos)
     */
    openLightbox() {
      const currentItem = this.currentItem;
      if (!currentItem || currentItem.type !== 'image') return;

      // Collect all image URLs (skip videos)
      const imageUrls = this.productImages
        .filter(item => item.type === 'image')
        .map(item => item.original);

      // Find current image index in images-only array
      const currentImageOnlyIndex = this.productImages
        .slice(0, this.currentImageIndex + 1)
        .filter(item => item.type === 'image')
        .length - 1;

      // Open lightbox with images
      window.Alpine.store('lightbox').open(
        currentItem.original,
        currentImageOnlyIndex,
        imageUrls
      );
    },

    // ========================================
    // CART & SHARE
    // ========================================

    /**
     * Add to cart with loading state
     */
    async addToCart(form) {
      if (this.loading.addToCart) return;

      this.loading.addToCart = true;

      try {
        // Use cart store for actual submission
        await window.Alpine.store('cart').addFromForm(form);
      } finally {
        this.loading.addToCart = false;
      }
    },

    /**
     * Share product
     */
    shareProduct() {
      const url = window.location.href;
      const title = document.title;

      if (navigator.share) {
        navigator
          .share({
            title: title,
            url: url,
          })
          .catch(() => {
            this.copyToClipboard(url);
          });
      } else {
        this.copyToClipboard(url);
      }
    },

    /**
     * Copy to clipboard
     */
    copyToClipboard(text) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          window.Notification?.success(window.tx('linkCopied', 'Link copied to clipboard!'));
        })
        .catch(() => {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = text;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);

          window.Notification?.success(window.tx('linkCopied', 'Link copied to clipboard!'));
        });
    },

    /**
     * Cleanup on destroy
     */
    destroy() {
      // Clean up any event listeners or timers
      this.loading = { addToCart: false };
    },
  };
}
